import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';
import { Field, controlClasses, describedBy } from './Field';

export interface DatePickerProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label: ReactNode;
  /** ISO `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` when `withTime`. */
  value?: string;
  onChange?: (value: string) => void;
  withTime?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  containerClassName?: string;
}

/**
 * A native date input.
 *
 * Deliberately not a hand-rolled calendar. The native control is keyboard
 * accessible, localised to the user's system settings, and correct about time zones
 * in a way that a bespoke calendar in a product handling limitation dates has no
 * business getting wrong.
 *
 * The value is an ISO date string throughout. Do not pass a `Date`: a `Date` carries
 * a time zone, and "the deadline is the 14th" must not become the 13th at 23:00 for
 * someone reading it in another office.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    label,
    value,
    onChange,
    withTime = false,
    hint,
    error,
    hideLabel,
    containerClassName,
    className,
    id: providedId,
    required,
    ...props
  },
  ref,
) {
  const id = useFieldId(providedId);

  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      <input
        ref={ref}
        id={id}
        type={withTime ? 'datetime-local' : 'date'}
        value={value ?? ''}
        onChange={(event) => onChange?.(event.target.value)}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(controlClasses(Boolean(error)), 'h-9', className)}
        {...props}
      />
    </Field>
  );
});
